import {
  useGetClicksAdvancedReportMutation,
  useGetConversionAdvancedReportMutation,
  useGetCostAdvancedReportMutation,
  useGetOpportunityAdvancedReportMutation,
  useGetRevenueAdvancedReportMutation
} from 'app/apis/advanced-reports/advanced-report-query-api';
import AdvancedReportsTable from 'features/advanced-reports/table/advanced-reports-table';
import AttributionCompareButton from 'features/attribute-compare-model/attributeCompareButton';
import AttributionButton from 'features/attribute-model/attributeButton';
import QuerySegmentsButton from 'features/segment-controls/query-segments/query-segments-button';
import ResetSegmentsButton from 'features/segment-controls/reset-segments/reset-segments-button';
import SegmentControls from 'features/segment-controls/segment-controls/segment-controls';
import Tile from 'features/tiles/tile';
import { useDispatch, useSelector } from 'react-redux';
import { AdvancedReportsQueryCategories } from 'features/advanced-reports/constants/advanced-reports-query-categories';
import { createCompareCacheKey } from 'features/advanced-reports/helpers/cache-helpers';
import { resetAdvancedReports } from 'features/advanced-reports/advanced-reports.middleware';
import { AdvancedReportQueryRequestDto } from 'features/advanced-reports/advanced-reports.types';
import { createAdvancedReportRequest } from 'features/segment-controls/factories/query-segments-request-factory';
import { selectSiteId } from 'features/topbar/selectedSiteSlice';
import { selectCurrencyCode } from 'features/settings/settingsSlice';
import {
  getSelectedAttributionCompareModelString,
  selectIsAttributionComparisonActive
} from 'features/attribute-compare-model/availableAttributionCompareModelsSlice';
import { RootState } from 'app/store';
import {
  selectCanRunQuery,
  selectSegmentFilters,
  selectValidSegments
} from 'features/segment-controls/segment-controls.slice';
import { getDateRangeAsObjects } from 'features/date-range-selection/dateRangeSlice';
import { getSelectedAttributionModel } from 'features/attribute-model/availableAttributionModelsSlice';
import { QueryStatus } from '@reduxjs/toolkit/query';

const AdvancedReportsPage = () => {
  const componentId: string = 'AdvancedReports';
  const canRunQuery = useSelector((state: RootState) =>
    selectCanRunQuery(state, componentId)
  );
  const dispatch = useDispatch();
  const siteId = useSelector(selectSiteId);
  const { isPocModeActive } = useSelector(
    (state: RootState) => state.pocToggle
  );
  const currencyCode = useSelector(selectCurrencyCode);
  const primaryAttributionModel = useSelector((state: RootState) =>
    getSelectedAttributionModel(state, isPocModeActive)
  );

  const isCompareSelected = useSelector(selectIsAttributionComparisonActive);
  const compareAttributionModel = useSelector((state: RootState) =>
    getSelectedAttributionCompareModelString(state, isPocModeActive)
  );

  const segments = useSelector((state: RootState) =>
    selectValidSegments(state, componentId)
  );
  const segmentFilters = useSelector((state: RootState) =>
    selectSegmentFilters(state, componentId)
  );
  const dates = useSelector(getDateRangeAsObjects);

  const createRequest = (
    queryCategory: AdvancedReportsQueryCategories,
    attributionModel: string
  ): AdvancedReportQueryRequestDto => {
    return createAdvancedReportRequest(
      siteId,
      queryCategory,
      currencyCode,
      attributionModel,
      segments,
      segmentFilters ?? [],
      dates
    );
  };

  const [getClicks, clicksResult] = useGetClicksAdvancedReportMutation({
    fixedCacheKey: AdvancedReportsQueryCategories.CLICKS
  });
  const [getConversionInboundCalls, callResult] =
    useGetConversionAdvancedReportMutation({
      fixedCacheKey: AdvancedReportsQueryCategories.CONVERSION_INBOUNDCALL
    });
  const [getConversionWebs, webResult] = useGetConversionAdvancedReportMutation(
    {
      fixedCacheKey: AdvancedReportsQueryCategories.CONVERSION_WEB
    }
  );
  const [getOpportunities, oppResult] = useGetOpportunityAdvancedReportMutation(
    {
      fixedCacheKey: AdvancedReportsQueryCategories.OPPORTUNITY
    }
  );
  const [getOpportunityValues, oppValueResult] =
    useGetOpportunityAdvancedReportMutation({
      fixedCacheKey: AdvancedReportsQueryCategories.OPPORTUNITY_VALUE
    });
  const [getSales, salesResult] = useGetRevenueAdvancedReportMutation({
    fixedCacheKey: AdvancedReportsQueryCategories.SALES
  });
  const [getRevenues, revenueResult] = useGetRevenueAdvancedReportMutation({
    fixedCacheKey: AdvancedReportsQueryCategories.REVENUE
  });
  const [getCosts, costResult] = useGetCostAdvancedReportMutation({
    fixedCacheKey: AdvancedReportsQueryCategories.AD_COST
  });
  const [getImpressions, impressionsResult] = useGetCostAdvancedReportMutation({
    fixedCacheKey: AdvancedReportsQueryCategories.IMPRESSIONS
  });
  const [compareGetImpressions, compareImpressionsResult] =
    useGetCostAdvancedReportMutation({
      fixedCacheKey: createCompareCacheKey(
        AdvancedReportsQueryCategories.IMPRESSIONS
      )
    });

  const [compareGetClicks, compareClicksResult] =
    useGetClicksAdvancedReportMutation({
      fixedCacheKey: createCompareCacheKey(
        AdvancedReportsQueryCategories.CLICKS
      )
    });
  const [compareGetCalls, compareCallResult] =
    useGetConversionAdvancedReportMutation({
      fixedCacheKey: createCompareCacheKey(
        AdvancedReportsQueryCategories.CONVERSION_INBOUNDCALL
      )
    });
  const [compareGetWebs, compareWebResult] =
    useGetConversionAdvancedReportMutation({
      fixedCacheKey: createCompareCacheKey(
        AdvancedReportsQueryCategories.CONVERSION_WEB
      )
    });
  const [compareGetOpps, compareOppResult] =
    useGetOpportunityAdvancedReportMutation({
      fixedCacheKey: createCompareCacheKey(
        AdvancedReportsQueryCategories.OPPORTUNITY
      )
    });
  const [compareGetOppalues, compareOppValueResult] =
    useGetOpportunityAdvancedReportMutation({
      fixedCacheKey: createCompareCacheKey(
        AdvancedReportsQueryCategories.OPPORTUNITY_VALUE
      )
    });
  const [compareGetSales, compareSalesResult] =
    useGetRevenueAdvancedReportMutation({
      fixedCacheKey: createCompareCacheKey(AdvancedReportsQueryCategories.SALES)
    });
  const [compareGetRevenues, compareRevenueResult] =
    useGetRevenueAdvancedReportMutation({
      fixedCacheKey: createCompareCacheKey(
        AdvancedReportsQueryCategories.REVENUE
      )
    });
  const [compareGetCosts, compareCostResult] = useGetCostAdvancedReportMutation(
    {
      fixedCacheKey: createCompareCacheKey(
        AdvancedReportsQueryCategories.AD_COST
      )
    }
  );

  const onConfirmResetSegments = () => {
    clicksResult.reset();
    callResult.reset();
    webResult.reset();
    oppResult.reset();
    oppValueResult.reset();
    salesResult.reset();
    revenueResult.reset();
    costResult.reset();
    impressionsResult.reset();
    compareImpressionsResult.reset();
    compareClicksResult.reset();
    compareCallResult.reset();
    compareWebResult.reset();
    compareOppResult.reset();
    compareOppValueResult.reset();
    compareSalesResult.reset();
    compareRevenueResult.reset();
    compareCostResult.reset();
    dispatch(resetAdvancedReports());
  };

  const runQuerySegmentsMutation = () => {
    const impressionsRequest = createRequest(
      AdvancedReportsQueryCategories.IMPRESSIONS,
      primaryAttributionModel
    );
    const clicksRequest = createRequest(
      AdvancedReportsQueryCategories.CLICKS,
      primaryAttributionModel
    );
    const conversionInboundCallRequest = createRequest(
      AdvancedReportsQueryCategories.CONVERSION_INBOUNDCALL,
      primaryAttributionModel
    );
    const conversionWebRequest = createRequest(
      AdvancedReportsQueryCategories.CONVERSION_WEB,
      primaryAttributionModel
    );
    const opportunityRequest = createRequest(
      AdvancedReportsQueryCategories.OPPORTUNITY,
      primaryAttributionModel
    );
    const opportunityValueRequest = createRequest(
      AdvancedReportsQueryCategories.OPPORTUNITY_VALUE,
      primaryAttributionModel
    );
    const salesRequest = createRequest(
      AdvancedReportsQueryCategories.SALES,
      primaryAttributionModel
    );
    const revenueRequest = createRequest(
      AdvancedReportsQueryCategories.REVENUE,
      primaryAttributionModel
    );
    const adCostRequest = createRequest(
      AdvancedReportsQueryCategories.AD_COST,
      primaryAttributionModel
    );
    void getImpressions(impressionsRequest);
    void getClicks(clicksRequest);
    void getConversionInboundCalls(conversionInboundCallRequest);
    void getConversionWebs(conversionWebRequest);
    void getOpportunities(opportunityRequest);
    void getOpportunityValues(opportunityValueRequest);
    void getSales(salesRequest);
    void getRevenues(revenueRequest);
    void getCosts(adCostRequest);

    if (isCompareSelected) {
      void compareGetImpressions(
        createRequest(
          AdvancedReportsQueryCategories.IMPRESSIONS,
          compareAttributionModel
        )
      );
      void compareGetClicks(
        createRequest(
          AdvancedReportsQueryCategories.CLICKS,
          compareAttributionModel
        )
      );
      void compareGetCalls(
        createRequest(
          AdvancedReportsQueryCategories.CONVERSION_INBOUNDCALL,
          compareAttributionModel
        )
      );
      void compareGetWebs(
        createRequest(
          AdvancedReportsQueryCategories.CONVERSION_WEB,
          compareAttributionModel
        )
      );
      void compareGetOpps(
        createRequest(
          AdvancedReportsQueryCategories.OPPORTUNITY,
          compareAttributionModel
        )
      );
      void compareGetOppalues(
        createRequest(
          AdvancedReportsQueryCategories.OPPORTUNITY_VALUE,
          compareAttributionModel
        )
      );
      void compareGetSales(
        createRequest(
          AdvancedReportsQueryCategories.SALES,
          compareAttributionModel
        )
      );
      void compareGetRevenues(
        createRequest(
          AdvancedReportsQueryCategories.REVENUE,
          compareAttributionModel
        )
      );
      void compareGetCosts(
        createRequest(
          AdvancedReportsQueryCategories.AD_COST,
          compareAttributionModel
        )
      );
    }
  };

  const isGettingQuery =
    clicksResult.status === QueryStatus.pending ||
    impressionsResult.status === QueryStatus.pending ||
    callResult.status === QueryStatus.pending ||
    webResult.status === QueryStatus.pending ||
    oppResult.status === QueryStatus.pending ||
    oppValueResult.status === QueryStatus.pending ||
    salesResult.status === QueryStatus.pending ||
    revenueResult.status === QueryStatus.pending ||
    costResult.status === QueryStatus.pending ||
    compareClicksResult.status === QueryStatus.pending ||
    compareImpressionsResult.status === QueryStatus.pending ||
    compareCallResult.status === QueryStatus.pending ||
    compareWebResult.status === QueryStatus.pending ||
    compareOppResult.status === QueryStatus.pending ||
    compareOppValueResult.status === QueryStatus.pending ||
    compareSalesResult.status === QueryStatus.pending ||
    compareRevenueResult.status === QueryStatus.pending ||
    compareCostResult.status === QueryStatus.pending;

  const isButtonEnabled =
    canRunQuery &&
    !impressionsResult.isLoading &&
    !clicksResult.isLoading &&
    !callResult.isLoading &&
    !webResult.isLoading &&
    !oppResult.isLoading &&
    !oppValueResult.isLoading &&
    !salesResult.isLoading &&
    !revenueResult.isLoading &&
    !costResult.isLoading &&
    !compareImpressionsResult.isLoading &&
    !compareClicksResult.isLoading &&
    !compareCallResult.isLoading &&
    !compareWebResult.isLoading &&
    !compareOppResult.isLoading &&
    !compareOppValueResult.isLoading &&
    !compareSalesResult.isLoading &&
    !compareRevenueResult.isLoading &&
    !compareCostResult.isLoading;

  return (
    <>
      <Tile className="grow">
        <SegmentControls componentId={componentId} />
        <div className="mt-6 flex flex-col md:flex-row">
          <div className="flex items-center justify-stretch">
            <AttributionButton dropDirection="right" width="w-60" />
            <AttributionCompareButton />
          </div>
        </div>
        <div className="mt-2 flex items-center">
          <QuerySegmentsButton
            enabled={isButtonEnabled}
            isLoading={isGettingQuery}
            onClick={runQuerySegmentsMutation}
          />
          <div className="ml-2">
            <ResetSegmentsButton
              componentId={componentId}
              onConfirmModal={onConfirmResetSegments}
            />
          </div>
        </div>
      </Tile>

      <div className="mt-4">
        <AdvancedReportsTable componentId={componentId} />
      </div>
    </>
  );
};

export default AdvancedReportsPage;
